import RoomOccupancy from './RoomOccupancy'
import Image from './Image'
import RoomService from './RoomService'

export default class Room {
  static fromJSON (json) {
    return new Room({
      id: json.id,
      code: json.code,
      name: json.name,
      tmsName: json.tmsName,
      description: json.description,
      defaultImage: Image.fromJSON(json.image),
      occupancy: RoomOccupancy.fromJSON(json.occupancy),
      bedType: json.bedType,
      size: json.size,
      services: (json.services || []).map(RoomService.fromJSON),
      stays: json.stays,
      jsonForIntegrateWithGallery: json
    })
  }

  NUMBER_OF_HIGHLIGHTED_STAYS = 4

  constructor ({ id, code, name, tmsName, description, defaultImage, occupancy, bedType, size, services, stays, jsonForIntegrateWithGallery }) {
    this.id = id
    this.code = code
    this.name = name
    this.tmsName = tmsName
    this.description = description
    this.defaultImage = defaultImage
    this.occupancy = occupancy || {}
    this.bedType = bedType
    this.size = size
    this.services = services || []
    this.stays = stays || []
    this.maxDiscount = this.stays.reduce((acc, stay) => stay.percentageOfDiscountPerNight > acc ? stay.percentageOfDiscountPerNight : acc, 0)
    this.isPayUponArrival = this.stays.filter(stay => stay.isPayUponArrival()).length > 0
    this.jsonForIntegrateWithGallery = jsonForIntegrateWithGallery
    this.numberOfRooms = Math.min(...this.stays.map(stay => stay.numberOfRoomsLeft))
  }

  hasAvailableStays () {
    return this.stays.length > 0
  }

  hasMoreStaysApartFromHighlighted () {
    return this.stays.length > 4
  }

  takeHighlightedStays () {
    return this.stays.slice(0, this.NUMBER_OF_HIGHLIGHTED_STAYS)
  }

  takeAllStays () {
    return this.stays
  }

  cheaperStay () {
    return this.hasAvailableStays() ? this.stays[0] : null
  }

  count (filter) {
    return this.stays.reduce((accumulation, stay) => {
      const matches = stay.count(filter)
      Object.keys(matches)
        .forEach((key) => {
          accumulation[key] = (accumulation[key] || 0) + matches[key]
        })
      return accumulation
    }, {})
  }

  applyFilter (filter, isLogged) {
    const stays = this.stays
      .map(stay => stay.applyFilter(filter))
      .filter(result => result.fullMatch)
      .map(result => result.stay)

    const sortedStays = filter.hasAnySelection()
      ? stays.sort((a, b) => a.orderBy.cashAndPoints - b.orderBy.cashAndPoints)
      : isLogged
        ? stays.sort((a, b) => a.orderBy.firstFourLoyalty - b.orderBy.firstFourLoyalty)
        : stays.sort((a, b) => a.orderBy.firstTwoLoyaltyAndTwoStandard - b.orderBy.firstTwoLoyaltyAndTwoStandard)

    return new Room({
      ...this,
      stays: sortedStays
    })
  }

  isAccessible () {
    return this.stays.filter(stay => stay.accessible).length > 0
  }

  changeCurrency (currency) {
    const stays = this.stays.map(stay => stay.changeCurrency(currency))
    return new Room({
      ...this,
      stays: stays
    })
  }

  hasGallery () {
    return this.jsonForIntegrateWithGallery && this.jsonForIntegrateWithGallery.gallery && this.jsonForIntegrateWithGallery.gallery.length > 0
  }

  jsonRoom () {
    return this.hasGallery() && this.jsonForIntegrateWithGallery
  }

  hasAnyStayWithPointsLessThan (points) {
    return this.stays.some(stay => stay.isPointsLessOrEqualThan(points))
  }

  getOnlyCashStays () {
    if (!this.stays) return []
    return this.stays.filter(stay => !stay.points.hasPoints() && !!stay.cash.hasCash())
  }

  getOnlyPointsStays (customerPoints) {
    if (!this.stays) return []
    return this.stays.filter(stay => !!stay.points.hasPoints() && !stay.cash.hasCash() && stay.points.amountPerNight <= customerPoints)
  }

  getCashAndPointsStays (customerPoints) {
    if (!this.stays) return []
    return this.stays.filter(stay => !!stay.points.hasPoints() && !!stay.cash.hasCash() && stay.points.amountPerNight <= customerPoints)
  }
}
